/**
 * The Company Registry step (KYB): find the company in the registry by its
 * registration number, pick it, confirm or complete the details — or enter it
 * by hand. The step's field modes arrive on `snapshot.step` from the pinned
 * workflow (server-side); the server re-fetches the registry record itself, so
 * nothing here is trusted beyond what the applicant typed.
 */
import { apiUrl, authHeaders, CollectionFlowError } from './client';

export const COMPANY_EVENT = 'COMPANY_SUBMITTED';

export type FieldMode = 'hidden' | 'optional' | 'required';

/** `snapshot.step` for a company step. */
export interface CompanyStepView {
  kind: 'company_registry';
  country: string;
  fields: Record<string, FieldMode>;
  search: { registration_number: boolean; name: boolean };
}

/** Display order — the server's `FIELDS`. Unknown server fields render last. */
export const FIELD_ORDER = [
  'registration_number',
  'incorporation_date',
  'legal_address',
  'vat_number',
  'alternative_names',
  'tax_number',
  'company_type',
  'legal_entity_identifier',
  'location_of_registration',
  'nature_of_business',
  'registered_capital_amount',
  'registered_capital_currency',
  'website',
  'email',
  'phone',
  'control_scheme',
] as const;

export interface CompanySearchResult {
  name: string;
  name_en: string | null;
  country: string;
  registration_number: string;
  /** `ACTIVE` | `INACTIVE` (the registry's word, passed through). */
  status: string;
  company_type: string | null;
  /** Registry values for the step's visible fields — display prefill only. */
  prefill: Record<string, string>;
}

export function companyStepOf(step: unknown): CompanyStepView | null {
  if (typeof step !== 'object' || step === null) return null;
  const s = step as Partial<CompanyStepView>;
  return s.kind === 'company_registry' && typeof s.fields === 'object' && s.fields !== null
    ? (s as CompanyStepView)
    : null;
}

/** The fields to render, in order: everything not hidden. */
export function visibleFields(view: CompanyStepView): { name: string; required: boolean }[] {
  const known = FIELD_ORDER.filter((f) => f in view.fields);
  const extra = Object.keys(view.fields).filter(
    (f) => !(FIELD_ORDER as readonly string[]).includes(f),
  );
  return [...known, ...extra]
    .filter((name) => view.fields[name] !== 'hidden')
    .map((name) => ({ name, required: view.fields[name] === 'required' }));
}

/** Digits only — dashes and spaces are how people copy the number. */
export function digitsOf(raw: string): string {
  return raw.replace(/\D/g, '');
}

/** Search the registry. A 4xx/5xx throws with the server's `code`. */
export async function searchCompany(
  token: string,
  query: string,
  by: 'registration_number' | 'name' = 'registration_number',
): Promise<CompanySearchResult[]> {
  const response = await fetch(apiUrl('/api/v1/collection-flow/company/search'), {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ query, by }),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: unknown;
    } | null;
    throw new CollectionFlowError(
      response.status,
      'collection-flow/company/search',
      typeof body?.code === 'string' ? body.code : undefined,
    );
  }
  return ((await response.json()) as { results: CompanySearchResult[] }).results;
}

/** The submission the server validates (`company_registry::CompanySubmission`). */
export interface CompanySubmission {
  source: 'registry' | 'manual';
  company_name?: string;
  fields: Record<string, string>;
}

/** Drop blanks: an empty optional field is "not given", not "". */
export function submissionOf(
  source: CompanySubmission['source'],
  companyName: string,
  values: Record<string, string>,
): CompanySubmission {
  const fields = Object.fromEntries(
    Object.entries(values)
      .map(([k, v]) => [k, v.trim()] as const)
      .filter(([, v]) => v !== ''),
  );
  const name = companyName.trim();
  return { source, ...(name ? { company_name: name } : {}), fields };
}
